import { createFileRoute } from "@tanstack/react-router";
import { Brush, Wrench, Hammer, TreePine, Shield, CheckCircle2 } from "lucide-react";
import { Layout, CallBanner } from "@/components/site/Layout";

export const Route = createFileRoute("/services")({
  head: () => ({
    meta: [
      { title: "Services — The Crew Paint Contractors | Arlington, TX" },
      { name: "description", content: "Painting, siding & wood replacement, drywall repair, interior trim, deck & fence staining in Arlington, TX." },
      { property: "og:title", content: "Our Services — The Crew Paint Contractors" },
      { property: "og:description", content: "Honest home improvement & maintenance services in Arlington, TX." },
      { property: "og:url", content: "https://thecrewpainting.com/services" },
    ],
    links: [{ rel: "canonical", href: "https://thecrewpainting.com/services" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: [
            "Siding & Wood Replacement",
            "Interior & Exterior Painting",
            "Drywall Repairs & Texture",
            "Interior Trim Replacement",
            "Deck & Fence Staining",
          ].map((name, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Service",
              name,
              serviceType: name,
              areaServed: { "@type": "City", name: "Arlington, TX" },
              provider: {
                "@type": "LocalBusiness",
                name: "The Crew Paint Contractors",
                telephone: "+1-682-454-5997",
              },
            },
          })),
        }),
      },
    ],
  }),
  component: Services,
});

function Services() {
  return (
    <Layout>
      <section className="mx-auto max-w-7xl px-6 pt-20 pb-12">
        <p className="text-xs uppercase tracking-widest text-accent font-semibold">Services</p>
        <h1 className="mt-3 font-display text-4xl md:text-5xl text-primary max-w-3xl">
          Our Home Improvement & Maintenance Services
        </h1>
        <p className="mt-5 text-lg text-muted-foreground max-w-2xl">
          A focused range of services done with the care and consistency of a true tradesman — not a sales pitch.
        </p>
      </section>

      {/* Featured */}
      <section className="mx-auto max-w-7xl px-6">
        <div className="rounded-3xl overflow-hidden border border-border bg-card grid lg:grid-cols-5">
          <div className="lg:col-span-2 bg-primary text-primary-foreground p-10 lg:p-12 flex flex-col justify-between min-h-[320px] relative">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_oklch(0.58_0.14_55/0.35),_transparent_60%)]" />
            <div className="relative">
              <span className="inline-block rounded-full bg-accent px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent-foreground">Featured Service</span>
              <Shield className="mt-8 h-10 w-10 text-accent" />
              <h2 className="mt-4 font-display text-3xl md:text-4xl">Siding & Wood Replacement</h2>
            </div>
          </div>
          <div className="lg:col-span-3 p-10 lg:p-12">
            <p className="text-lg text-foreground/85 leading-relaxed">
              Damaged siding and rotted exterior wood don't just look bad — they let moisture into your home and threaten its long-term value. We replace what's failing with materials built to last, then prep and paint it to seal everything in.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3 text-sm text-foreground/80">
              {[
                "Full siding inspection & assessment",
                "Rotted wood & trim replacement",
                "Color-matched finishes",
                "Sealed to permanently protect your home",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-accent mt-0.5 shrink-0" /> {t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Other services grid */}
      <section className="mx-auto max-w-7xl px-6 mt-16">
        <h2 className="font-display text-2xl md:text-3xl text-primary">Additional Services</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {[
            { Icon: Brush, title: "Interior & Exterior Painting", body: "Crisp, clean finishes inside and out. Proper prep, premium paints, and a sharp line every time." },
            { Icon: Wrench, title: "Drywall Repairs & Texture Matching", body: "From small dings to whole panels — we patch, retexture, and blend so you can't tell where the damage was." },
            { Icon: Hammer, title: "Interior Trim Replacement", body: "Baseboards, casing, and crown molding installed and finished to look original to the home." },
            { Icon: TreePine, title: "Deck & Fence Staining", body: "Cleaned, prepped, and properly stained to handle the Texas sun and protect the wood for years." },
          ].map(({ Icon, title, body }) => (
            <div key={title} className="rounded-xl border border-border bg-card p-7 hover:border-primary/30 transition">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/8 text-primary">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 font-display text-xl text-primary">{title}</h3>
              <p className="mt-2 text-muted-foreground leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <CallBanner text="Ready to protect and beautify your home? Call Bart directly at 682-454-5997." />
    </Layout>
  );
}
